import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// @mui
import {
    Typography,
    TableCell,
    Stack,
    TableContainer,
    Table,
    TableRow,
    TableBody,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    Button,
    Grid,
} from '@mui/material';
// components
import Iconify from '../../components/iconify';
import ItemNotFound from '../../components/ItemNotFound';

// ----------------------------------------------------------------------

ProgrammeListPage.propTypes = {
    row: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    selected: PropTypes.bool,
    programmeList: PropTypes.array,
};

export default function ProgrammeListPage({ row, selected, programmeList }) {
    const [openDialog, setOpenDialog] = useState(false);
    const [tableData, setTableData] = useState([]);

    const handleAbbreviations = () => {
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    useEffect(() => {
        if (row) {
            const initialTableData = Array.isArray(row) ? row : [row];
            setTableData(initialTableData.filter(item => item && typeof item === 'object'));
        }
    }, [row]);

    const programmeMarkingMap = tableData.reduce((acc, mark) => {
        if (mark && mark.Year) {
            acc[mark.Year] = acc[mark.Year] || {};
            programmeList.forEach(programme => {
                acc[mark.Year][programme.ProgrammeSlug] = mark[programme.ProgrammeSlug] || '-';
            });
        }
        return acc;
    }, {});

    const getTotalMarkForYear = (year) => {
        const yearData = programmeMarkingMap[year] || {};
        return Object.values(yearData).reduce((acc, mark) => acc + (mark !== '-' ? mark : 0), 0);
    };

    const uniqueYears = [...new Set(tableData.map(data => data.Year).filter(year => year))];

    return (
        <>
            <Helmet>
                <title>MAGScholar | User List</title>
            </Helmet>

            <Container>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5} onClick={handleAbbreviations} sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                    <Iconify icon="clarity:pop-out-line" width={10} />
                    <Typography variant="caption" fontWeight={600}>Abbreviations</Typography>
                </Stack>

                <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                    <Table size="medium" sx={{ minWidth: 800 }}>
                        <TableBody>
                            <TableRow hover selected={selected}>
                                <TableCell align="center">
                                    <Typography variant="caption" fontWeight={600} sx={{ wordBreak: 'break-word' }}>Programmes</Typography>
                                </TableCell>

                                {programmeList.map((programme) => (
                                    <TableCell align="center" key={programme.ProgrammeID}>
                                        <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{programme.ProgrammeSlug || ' '} ({programme.Mark}pt)</Typography>
                                    </TableCell>
                                ))}

                                <TableCell align="center">
                                    <Typography variant="caption" fontWeight={500} sx={{ wordBreak: 'break-word' }}>Total</Typography>
                                </TableCell>
                            </TableRow>

                            {uniqueYears.length > 0 ? (
                                uniqueYears.map((year) => (
                                    <TableRow hover selected={selected} key={year}>
                                        <TableCell align="center">
                                            <Typography variant="caption" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{year}</Typography>
                                        </TableCell>

                                        {programmeList.map((programme) => (
                                            <TableCell align="center" key={programme.ProgrammeID}>
                                                {programmeMarkingMap[year] && programmeMarkingMap[year][programme.ProgrammeSlug] !== '-' ? (
                                                    <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                                                        {programmeMarkingMap[year][programme.ProgrammeSlug]}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        ))}

                                        <TableCell align="center">
                                            <Typography variant="caption" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                                                {getTotalMarkForYear(year) || '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={programmeList.length + 2} align="center">
                                        <ItemNotFound title="No Data Available" />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>

            <Dialog open={openDialog} onClose={handleClose}>
                <DialogTitle>
                    <Typography variant="subtitle1">Certified MAGScholar© Programme</Typography>
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {programmeList.map((programme) => (
                            <Grid container key={programme.ProgrammeID} mb={0.5}>
                                <Grid item md={3}>
                                    <Typography variant="subtitle2">
                                        {programme.ProgrammeSlug}
                                    </Typography>
                                </Grid>
                                <Grid item md={8}>
                                    <Typography variant="body2" textAlign="left">
                                        {programme.ProgrammeName}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ))}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
